// ============================================================
// TMR Portal Admin — edição do cadastro da empresa
// ============================================================
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorState, Skeleton } from '../../components/states';
import { useUpdateCompany } from '../../data/companies/queries';
import {
  fromRecord,
  representativeChanged,
  toBankDetails,
  toUpdateRequest,
} from '../../data/companies/form';
import type { CompanyForm } from '../../data/companies/form';
import type { CompanyRecord } from '../../data/companies/contract';
import {
  defaultAccount,
  useAddReceivingAccount,
  useReceivingAccounts,
  useUpdateReceivingAccount,
} from '../../data/finance/queries';
import type { ReceivingAccount } from '../../data/finance/contract';
import {
  StepAddress,
  StepCompanyGeneral,
  StepContactAndRepresentative,
  StepRates,
  StepReceivingAccount,
  validateCompanyForm,
} from './steps';
import type { CompanyFormErrors, SetField } from './steps';

/** Props for {@link CompanyEditor}. */
interface CompanyEditorProps {
  /** The record being edited — the form starts from what is actually saved. */
  company: CompanyRecord;
  onCancel: () => void;
  onSaved: () => void;
  /**
   * Reports whether the form has unsaved changes. The detail screen owns every way out of the editor
   * (the back link, the tabs, "Novo contrato") and uses this to ask before throwing the edit away.
   */
  onDirtyChange: (dirty: boolean) => void;
}

/**
 * The Empresa's registration, editable.
 *
 * The screen looks like one form but writes to **two** resources: the registration goes to
 * `PUT /companies/{id}`, and the receiving account to the Finance module, which owns bank details since
 * B8.2 (13). They are saved in that order and the editor only closes when both succeeded, so a failure
 * on the second never reads as "saved".
 *
 * The account is loaded before the form opens because it seeds the bank fields — starting the form from
 * blanks and then filling them in would let an operator save over an account they never saw.
 */
export function CompanyEditor({ company, onCancel, onSaved, onDirtyChange }: CompanyEditorProps) {
  const accounts = useReceivingAccounts(company.id);

  if (accounts.isPending) return <Skeleton rows={6} />;
  // Without the account the bank fields would open blank and a save could overwrite what is stored.
  // `finance.read` missing lands here too, with its own wording, and editing stays closed.
  if (accounts.isError) {
    return <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />;
  }

  return (
    <CompanyEditorForm
      company={company}
      account={defaultAccount(accounts.data)}
      onCancel={onCancel}
      onSaved={onSaved}
      onDirtyChange={onDirtyChange}
    />
  );
}

/** The form itself, mounted only once the Empresa's account is known (or known to be absent). */
function CompanyEditorForm({
  company,
  account,
  onCancel,
  onSaved,
  onDirtyChange,
}: CompanyEditorProps & { account: ReceivingAccount | null }) {
  const [form, setForm] = useState<CompanyForm>(() => fromRecord(company, account));
  const [showErrors, setShowErrors] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const update = useUpdateCompany(company.id);
  const addAccount = useAddReceivingAccount(company.id);
  const updateAccount = useUpdateReceivingAccount(company.id);

  const set: SetField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // The pristine snapshot the form opened from — `fromRecord` is deterministic, so recomputing it here
  // gives the same values `useState` seeded with. Every field is a string or boolean, so a per-field
  // comparison is enough to tell whether the operator has actually changed anything.
  const initial = useMemo(() => fromRecord(company, account), [company, account]);
  const dirty = useMemo(
    () => (Object.keys(initial) as (keyof CompanyForm)[]).some((key) => form[key] !== initial[key]),
    [form, initial],
  );
  // Report unsaved state up so the detail screen can guard every way out of the editor…
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  // …and clear it on unmount (saved, discarded, or navigated away): there is nothing left to lose.
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  // The CNPJ is not part of an edit, so it is not part of what is checked.
  const errors: CompanyFormErrors = validateCompanyForm(form, { requireCnpj: false });
  const details = toBankDetails(form);
  // An account cannot be removed from here — the backend has no delete, because a Company's only
  // account disappearing is not a state it allows (13 §8). Clearing the fields has to say so rather
  // than save the registration and quietly leave the old account in place.
  if (account && !details) {
    errors.bankName = 'A conta de recebimento não pode ser removida por aqui';
  }
  const hasErrors = Object.keys(errors).length > 0;

  const saving = update.isPending || addAccount.isPending || updateAccount.isPending;

  // Replacing the legal representative is not a cadastre correction: the contracts that named the previous
  // one are closed and a new one is drawn up, because the Empresa would otherwise stay bound by a document
  // somebody else signed. Worth asking about before it happens, not explaining afterwards.
  const replacingRepresentative = representativeChanged(initial, form);

  const commit = async () => {
    try {
      await update.mutateAsync(toUpdateRequest(form));

      if (details) {
        if (account) {
          await updateAccount.mutateAsync({ accountId: account.id, details });
        } else {
          // The Empresa's first account is always the default, whatever this flag says — sending it
          // makes the intent explicit rather than relying on that.
          await addAccount.mutateAsync({ account: details, isDefault: true });
        }
      }

      onSaved();
    } catch {
      // The failing mutation carries the message; `saveError` below renders it.
    }
  };

  const save = () => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }

    if (replacingRepresentative) {
      setConfirming(true);
      return;
    }

    void commit();
  };

  const sectionProps = { form, set, errors: showErrors ? errors : {} };
  const saveError = [update.error, addAccount.error, updateAccount.error].find(
    (error): error is ApiError => error instanceof ApiError,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {saveError && (
        <div role="alert" style={{ padding: '12px 14px', background: 'oklch(96% 0.03 28)', border: '1px solid oklch(80% 0.09 28)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'oklch(38% 0.12 28)' }}>
          {saveError.detail}
        </div>
      )}

      <div style={{ padding: '10px 14px', background: 'var(--cream-100)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: 12.5, color: 'var(--fg-muted)' }}>
        O CNPJ não pode ser alterado — ele identifica a empresa. As alterações são replicadas logo após
        salvar.
      </div>

      <div className="pa-card" style={{ padding: 20 }}>
        <div className="pa-eyebrow" style={{ marginBottom: 14 }}>Empresa</div>
        <StepCompanyGeneral {...sectionProps} cnpjLocked />
      </div>
      <div className="pa-card" style={{ padding: 20 }}>
        <div className="pa-eyebrow" style={{ marginBottom: 14 }}>Endereço</div>
        <StepAddress {...sectionProps} />
      </div>
      <div className="pa-card" style={{ padding: 20 }}>
        <div className="pa-eyebrow" style={{ marginBottom: 14 }}>Contato & representante</div>
        <StepContactAndRepresentative {...sectionProps} />
      </div>
      {/* Duas seções, não uma: a conta é para onde o dinheiro da empresa vai e é salva com o cadastro; as
          taxas são o que a TMR cobra e esta tela não as salva. Juntas sob "Recebimentos", a segunda parecia
          ser gravada junto com a primeira. */}
      <div className="pa-card" style={{ padding: 20 }}>
        <div className="pa-eyebrow" style={{ marginBottom: 14 }}>Conta de recebimento</div>
        <StepReceivingAccount {...sectionProps} />
      </div>
      <div className="pa-card" style={{ padding: 20 }}>
        <div className="pa-eyebrow" style={{ marginBottom: 14 }}>Taxas</div>
        <StepRates {...sectionProps} />
      </div>

      {/* Barra de ações fixa: acompanha a rolagem do formulário e fica sempre visível no rodapé.
          As margens negativas sangram até as bordas do conteúdo (a `.pa-screen` que a hospeda tem
          padding 24), e a de baixo cancela o padding inferior para a barra encostar no fim da tela. */}
      <div
        className="pa-editor-actionbar"
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          margin: '2px -24px -24px',
          padding: '13px 24px',
          background: 'var(--cream-50)',
          borderTop: '1px solid var(--border-default)',
          boxShadow: '0 -6px 18px -10px rgba(28, 30, 28, 0.22)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--fg-muted)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: dirty ? 'var(--gold-400)' : 'var(--stone-300)' }} />
          {dirty ? 'Alterações não salvas' : 'Nenhuma alteração pendente'}
        </span>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="secondary" onClick={onCancel} disabled={saving}>
            Descartar alterações
          </Btn>
          <Btn variant="primary" onClick={saving ? undefined : save} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </Btn>
        </div>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Trocar o representante legal?"
          message={
            <>
              Quem assina pela empresa passa a ser{' '}
              <strong>{form.representativeName.trim() || 'a nova pessoa informada'}</strong>. Um contrato
              novo é criado para ela e os contratos ainda não enviados são cancelados. Contratos já
              assinados continuam no histórico, e o envio do novo segue sendo um passo à parte.
            </>
          }
          confirmLabel="Trocar e salvar"
          onConfirm={() => void commit()}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
